/**
 * Цена ПРО на ру в валюте посетителя — для кнопки «Открыть ПРО» в кабинете
 * и плашки акции «первый месяц за 5 $» на /pricing.
 *
 * Источники те же, что у окна оплаты: обычная цена — GET /api/stripe/pro-prices
 * (useStripeProPrices), право на акцию — поле pro5_offer из /auth/me
 * (stripe_pro_offer.py на бэкенде). Цена первого месяца по акции — 5 $ /
 * 2 500 ₸ / 5 €, в остальных валютах половина обычной: ровно как считает
 * сервер (stripe_pro_offer.promo_amount); на кассе сумму всё равно ставит он.
 */
import { IS_ROBOKASSA, STRIPE_ENABLED } from '@/lib/siteConfig';
import { STRIPE_PRIMARY, stripeDefaultCurrency } from '@/lib/visitorRegion';
import { formatStripeAmount } from '@/components/payment/StripePaymentModal';
import { CURRENCY_SYMBOLS } from '@/hooks/useCurrency';

const PRO5_PRICES = { USD: 5, KZT: 2500, EUR: 5 };

/** Куда ведёт любой вход в покупку ПРО у человека с правом на 5 $: та же
 * оплата Stripe, что у главной кнопки письма pro5_ru (страница тарифов сама
 * открывает окно Stripe на месяц ПРО по акции). */
export const PRO5_PATH = '/pricing?offer=pro5';

/** Есть ли у вошедшего ДЕЙСТВУЮЩЕЕ право на «первый месяц за 5 $» (только
 * ру со Stripe). Право и его срок считает сервер (/auth/me → pro5_offer;
 * истекло или ПРО уже куплен — поля нет). Здесь — страховка на старый ответ
 * из кеша: срок прошёл или ПРО уже оплачен — права нет. */
export function hasPro5Offer(user, now = Date.now()) {
  if (!(IS_ROBOKASSA && STRIPE_ENABLED && user && user.pro5_offer)) return false;
  const until = Date.parse(user.pro5_offer.until || '');
  if (!Number.isFinite(until) || until <= now) return false;
  const paidPro = Number(user.effective_tier) >= 3 && user.tier_source !== 'trial';
  return !paidPro;
}

/** «5 $» / «2 500 ₸» — цена первого месяца по акции в валюте посетителя по
 * умолчанию (та же валюта, что откроет окно Stripe). */
export function pro5DefaultLabel(currency = stripeDefaultCurrency()) {
  const cur = String(currency || 'USD').toUpperCase();
  const amount = PRO5_PRICES[cur] != null ? PRO5_PRICES[cur] : PRO5_PRICES.USD;
  return formatStripeAmount(amount, CURRENCY_SYMBOLS[PRO5_PRICES[cur] != null ? cur : 'USD']);
}

/** Касса Stripe — главная: иностранец на ру или человек с правом на акцию
 * (письмо обещает 5 $, и касса должна быть та, где эти 5 $). */
export function stripeIsPrimary(user) {
  return STRIPE_PRIMARY || hasPro5Offer(user);
}

export function pro5Amount(currency, regular) {
  const fixed = PRO5_PRICES[String(currency || '').toUpperCase()];
  const amount = fixed != null ? fixed : Math.round((Number(regular) / 2) * 100) / 100;
  return amount < Number(regular) ? amount : null;
}

/** «9,99 $» по ответу useStripeProPrices, или '' — цены ещё нет. */
export function monthlyPriceLabel(proPrices) {
  const m = proPrices && proPrices.status === 'ready' && proPrices.plans && proPrices.plans.PRO_MONTHLY;
  if (!m) return '';
  return formatStripeAmount(Number(m.amount), m.symbol || CURRENCY_SYMBOLS[m.currency] || m.currency);
}

/** Сумма первого месяца по акции в валюте карточки, или ''. */
export function pro5PriceLabel(proPrices) {
  const m = proPrices && proPrices.status === 'ready' && proPrices.plans && proPrices.plans.PRO_MONTHLY;
  if (!m) return '';
  const amount = pro5Amount(m.currency, m.amount);
  if (amount == null) return '';
  return formatStripeAmount(amount, m.symbol || CURRENCY_SYMBOLS[m.currency] || m.currency);
}
